<template>
  <b-row class="activity-summary">
    <b-col
      v-for="tile in tiles"
      :key="tile.label"
      cols="6"
      md="3"
      class="mb-1"
    >
      <!--
        Each tile is also a filter. The number is the answer to "how many failures this week";
        clicking it is the follow-up question, so the two are the same control rather than a
        statistic you then have to go and reproduce in the dropdowns.
      -->
      <b-card
        no-body
        class="h-100 summary-tile"
        :class="{ 'summary-tile--active': isActive(tile) }"
        @click="$emit('apply-view', tile.filters)"
      >
        <b-card-body class="d-flex align-items-center py-1">
          <b-avatar :variant="tile.variant" size="2.5rem" class="mr-1 flex-shrink-0">
            <feather-icon :icon="tile.icon" size="18" />
          </b-avatar>
          <div class="overflow-hidden">
            <h4 class="mb-0 font-weight-bolder">{{ formatNumber(tile.value) }}</h4>
            <small class="text-muted text-nowrap">{{ tile.label }}</small>
          </div>
        </b-card-body>
      </b-card>
    </b-col>

    <!--
      A plain per-day bar strip rather than a chart library.

      It answers one question — "is today unusual?" — and a spike being visible at a glance is the
      whole value. Pulling in a charting dependency for a single sparkline would cost more than it
      returns, and the failure overlay is the part that actually matters.
    -->
    <b-col v-if="timeline.length > 1" cols="12" class="mb-1">
      <b-card no-body class="h-100">
        <b-card-body class="py-1">
          <div class="d-flex justify-content-between align-items-baseline mb-50">
            <small class="text-muted">Activity per day</small>
            <small class="text-muted">
              peak {{ formatNumber(peak) }} · failures in red
            </small>
          </div>
          <div class="timeline-strip">
            <div
              v-for="day in timeline"
              :key="day.date"
              v-b-tooltip.hover
              class="timeline-bar"
              :title="`${day.date}: ${day.total} entries, ${day.failures} failed`"
              :style="{ height: `${barHeight(day.total)}%` }"
            >
              <!-- Failures are drawn inside the day's bar, so the ratio is visible, not just the count. -->
              <div
                v-if="day.failures"
                class="timeline-bar__failures"
                :style="{ height: `${(day.failures / day.total) * 100}%` }"
              />
            </div>
          </div>
        </b-card-body>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import {
  BRow, BCol, BCard, BCardBody, BAvatar, VBTooltip,
} from 'bootstrap-vue'

import { get } from 'lodash'

/**
 * The summary strip above the activity table.
 *
 * Reads the same `stats` payload the table's filter produced, so the counts always describe
 * exactly the rows below them — a strip showing unfiltered totals above a filtered table is worse
 * than no strip, because it quietly invites the wrong conclusion.
 */
export default {
  name: 'ActivityLogSummary',
  directives: { 'b-tooltip': VBTooltip },
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BAvatar,
  },
  props: {
    stats: {
      type: Object,
      default: () => ({}),
    },
    /** The page's live filter, so an already-applied tile can show as active. */
    filters: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    tiles() {
      const byOutcome = get(this.stats, 'by_outcome', {});
      const bySeverity = get(this.stats, 'by_severity', {});
      const byCategory = get(this.stats, 'by_category', {});

      return [
        {
          label: 'Total entries',
          value: get(this.stats, 'total', 0),
          icon: 'ActivityIcon',
          variant: 'light-primary',
          filters: {},
        },
        {
          label: 'Data changes',
          value: get(byCategory, 'data_change', 0),
          icon: 'EditIcon',
          variant: 'light-info',
          filters: { category: 'data_change' },
        },
        {
          label: 'Failed',
          value: get(byOutcome, 'failure', 0),
          icon: 'AlertTriangleIcon',
          variant: 'light-danger',
          filters: { outcome: 'failure' },
        },
        {
          label: 'Denied / critical',
          // Two different problems, but a reader scanning for trouble wants them together:
          // both mean "someone was stopped, or something serious happened".
          value: get(byOutcome, 'denied', 0) + get(bySeverity, 'critical', 0),
          icon: 'ShieldOffIcon',
          variant: 'light-warning',
          filters: { outcome: 'denied' },
        },
      ];
    },
    timeline() {
      return get(this.stats, 'timeline', []);
    },
    peak() {
      return this.timeline.reduce((max, day) => Math.max(max, day.total), 0);
    },
  },
  methods: {
    /**
     * Bars are scaled against the window's own peak rather than an absolute scale, so the shape
     * of the week is readable whether the system saw 50 entries or 50,000.
     */
    barHeight(total) {
      if (!this.peak) return 0;
      // A floor of 4% keeps a quiet-but-nonzero day visible instead of indistinguishable from none.
      return Math.max(4, Math.round((total / this.peak) * 100));
    },
    formatNumber(value) {
      return new Intl.NumberFormat().format(value || 0);
    },
    isActive(tile) {
      const keys = Object.keys(tile.filters);
      if (keys.length === 0) return false;
      return keys.every((key) => this.filters[key] === tile.filters[key]);
    },
  },
};
</script>

<style lang="scss" scoped>
.summary-tile {
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease;

  &:hover {
    transform: translateY(-2px);
  }

  &--active {
    box-shadow: 0 0 0 2px var(--primary);
  }
}

.timeline-strip {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 48px;
}

.timeline-bar {
  flex: 1 1 0;
  min-width: 2px;
  background-color: rgba(115, 103, 240, 0.45);
  border-radius: 2px 2px 0 0;
  position: relative;
  display: flex;
  align-items: flex-end;

  &:hover {
    background-color: rgba(115, 103, 240, 0.8);
  }

  &__failures {
    width: 100%;
    background-color: rgba(234, 84, 85, 0.85);
    border-radius: 2px 2px 0 0;
  }
}
</style>
